import { type ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, CheckCircle2, Info, X, AlertTriangle } from 'lucide-react'
import { cn } from '@/shared/utils/cn'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastItem {
  id: string
  message: ReactNode
  type: ToastType
}

type Listener = (items: ToastItem[]) => void

let toasts: ToastItem[] = []
const listeners = new Set<Listener>()

function emit() {
  for (const listener of listeners) listener(toasts)
}

function pushToast(message: ReactNode, type: ToastType = 'info') {
  const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`
  toasts = [...toasts, { id, message, type }]
  emit()
  window.setTimeout(() => dismissToast(id), 3200)
  return id
}

export function dismissToast(id: string) {
  toasts = toasts.filter((toast) => toast.id !== id)
  emit()
}

export const showToast = {
  success: (message: ReactNode) => pushToast(message, 'success'),
  error: (message: ReactNode) => pushToast(message, 'error'),
  warning: (message: ReactNode) => pushToast(message, 'warning'),
  info: (message: ReactNode) => pushToast(message, 'info'),
}

const icon = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
}

const toneClass = {
  success: 'border-success/20 bg-success-soft text-success',
  error: 'border-danger/20 bg-danger-soft text-danger',
  warning: 'border-warning/20 bg-warning-soft text-warning',
  info: 'border-brand/20 bg-brand-soft text-brand',
}

export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>(toasts)

  useEffect(() => {
    listeners.add(setItems)
    return () => {
      listeners.delete(setItems)
    }
  }, [])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed left-1/2 top-[calc(var(--safe-top)+16px)] z-10000 flex w-[min(420px,calc(100vw-32px))] -translate-x-1/2 flex-col gap-2"
    >
      {items.map((toast) => {
        const Icon = icon[toast.type]
        return (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex items-center gap-3 rounded-lg border px-4 py-3 shadow-overlay',
              toneClass[toast.type],
            )}
          >
            <Icon aria-hidden className="size-4 shrink-0" />
            <div className="flex min-h-6 min-w-0 flex-1 items-center text-preset-caption font-semibold leading-[1.35] text-text">
              {toast.message}
            </div>
            <button
              type="button"
              className="flex size-6 shrink-0 items-center justify-center rounded-full text-text-subtle hover:bg-white/60"
              onClick={() => dismissToast(toast.id)}
              aria-label="토스트 닫기"
            >
              <X aria-hidden className="size-4" />
            </button>
          </div>
        )
      })}
    </div>,
    document.body,
  )
}
